/**
 * The patient a builder test call is about (ADR-275): fictional, fixed, and
 * the same for everyone, so a test needs no real chart and exposes nobody's.
 *
 * On a test call the worker's identity lookup knows ONLY this patient
 * (`testPatientIdentity`, ADR-335), so identity steps run as written and
 * recognise "Taylor Test, 12 April 1985" and nobody else — no real chart is
 * searched, whatever the tester says. A step test stands identity in as this
 * patient. Writes are recorded, not made (ADR-306): a booking succeeds as on a
 * call and is read back, and finding an appointment finds the made-up one the
 * tester described, or none. The worker attaches no patient to the interaction
 * and files no follow-up task.
 *
 * No phone number, on purpose: a phone or text-confirmation step then asks
 * the tester for one instead of settling on an invented line.
 */
/** Who a builder test call is about: the fixed test patient today (ADR-335). */
export interface TestPatient {
  id: string;
  firstName: string;
  lastName: string;
  /** YYYY-MM-DD. */
  dateOfBirth: string;
  addressLine: string | null;
  insurance: string | null;
}

export const BUILDER_TEST_PATIENT = {
  id: '00000000-0000-4000-8000-00000000fe57',
  firstName: 'Taylor',
  lastName: 'Test',
  dateOfBirth: '1985-04-12',
  addressLine: '100 Test Street, Springfield',
  insurance: 'Test Health PPO',
} as const satisfies TestPatient;

/**
 * The id of the made-up appointment a builder test's patient "has" (ADR-306),
 * when the tester describes one. Like the patient, it matches no row: the
 * test's scheduling port answers with it and never asks the real one.
 */
export const BUILDER_TEST_APPOINTMENT_ID = '00000000-0000-4000-8000-0000000a9957';

/**
 * Component categories whose step test runs WITHOUT identity stood in: the
 * step opens a call, or is itself about identity. That step then asks who is
 * calling, and only `BUILDER_TEST_PATIENT` is recognised. Read by the API when
 * it wraps a step test and by the builder when it says what the test will do.
 */
const STEP_TEST_ASKS_IDENTITY = new Set(['open', 'identity']);

/** Whether a step test of this category stands identity in as the test patient. */
export function stepTestStandsInIdentity(category: string | undefined): boolean {
  return !STEP_TEST_ASKS_IDENTITY.has(category ?? '');
}
